import type {
  locationSummarySchema,
  resourceCreateRequestSchema,
  resourceDetailResponseSchema,
  resourcePatchRequestSchema,
  resourceResponseSchema,
  resourceWithCurrentSchemesSchema,
} from '@tempo-io/capacity-dto';
import type { z } from 'zod';

export const EMPLOYMENT_TYPE = {
  EMPLOYEE: 'EMPLOYEE',
  CONTRACTOR: 'CONTRACTOR',
  PARTNER: 'PARTNER',
  ON_DEMAND: 'ON_DEMAND',
  AI_AGENT: 'AI_AGENT',
} as const;

export type Resource = z.infer<typeof resourceResponseSchema>;

export type EmploymentType = Resource['employmentType'];

// AI_AGENT is excluded: employmentType is create-only immutable to/from AI_AGENT
// (see ResourceEmploymentTypeGuard), so it must never appear as a selectable option in
// edit or bulk-edit. The create dialog composes AI_AGENT back in itself, flag-gated.
// Use this instead of Object.values(EMPLOYMENT_TYPE) wherever employmentType is
// rendered as user-selectable options.
export const SELECTABLE_EMPLOYMENT_TYPES = [
  EMPLOYMENT_TYPE.EMPLOYEE,
  EMPLOYMENT_TYPE.CONTRACTOR,
  EMPLOYMENT_TYPE.PARTNER,
  EMPLOYMENT_TYPE.ON_DEMAND,
] as const satisfies readonly EmploymentType[];

// The resource shape returned with its current holiday/workload schemes attached.
export type ResourceWithCurrentSchemes = z.infer<typeof resourceWithCurrentSchemesSchema>;

export type LocationSummary = z.infer<typeof locationSummarySchema>;

export type ResourceWithDetails = z.infer<typeof resourceDetailResponseSchema>;

export const RESOURCE = 'RESOURCE';

export type ResourceType = Resource['type'];

export type CreateResourceRequest = z.infer<typeof resourceCreateRequestSchema>;

export type UpdateResourceRequest = z.infer<typeof resourcePatchRequestSchema>;

export type PageResponse<T> = {
  data: T[];
  recordLimit: number | null;
  recordCount: number;
  cursor: string | null;
  hasNextPage: boolean;
};
